import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useTran, useFormDirty } from "@/hooks";
import { useAuthStore } from "@/features/auth/hooks";
import AppDialog from "@/components/common/AppDialog";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SelectField } from "@/components/common";
import EntryRowTable from "../entries/EntryRowTable";
import {
  makeTreewithAllowOrgIds,
  toApiOrgId,
} from "../../utils/orgTree.helpers";
import { evaluateFormula } from "@/features/esg/utils/formula-evaluator";
import { observer } from "mobx-react-lite";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
  useValidationStore,
} from "../../hooks/useDataEntryStore";
import { bulkCreateDataEntries } from "../../operations/entry.ops";
import { showToast } from "@/lib/utils/toast";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";

const getReportingYearId = (reportingYear = {}) =>
  reportingYear._id || reportingYear.id || reportingYear.reportingYearId;

const getYearsInReportingYear = (reportingYear = {}) => {
  const startYear = Number(reportingYear.startYear);
  const endYear = Number(reportingYear.endYear ?? reportingYear.startYear);
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];

  const years = [];
  for (let year = startYear; year <= endYear; year += 1) {
    years.push(year);
  }
  return years;
};

const getOrganizationId = (organization) =>
  organization && typeof organization === "object"
    ? organization._id || organization.id || organization.organizationId
    : organization;

const buildAllowedOrganizationSet = ({
  records = [],
  reportingYear,
  selectedYear,
  companyId,
}) => {
  if (!selectedYear || !reportingYear) return null;

  const combine = (left, right) => {
    if (!left) return right;
    if (!right) return left;
    return new Set([...left].filter((id) => right.has(id)));
  };

  const reportingYearScope = reportingYear.isAllOrganization
    ? null
    : new Set(
        [
          ...(reportingYear.isCompanySelected && companyId ? [companyId] : []),
          ...(reportingYear.organizations || []).map(getOrganizationId),
        ]
          .filter(Boolean)
          .map(String),
      );

  const yearRecords = records.filter(
    (record) => Number(record.year) === Number(selectedYear),
  );
  const disclosureHasAllOrganizations = yearRecords.some(
    (record) => record.isAllOrganization,
  );
  const disclosureScope = disclosureHasAllOrganizations
    ? null
    : new Set(
        yearRecords
          .flatMap((record) => [
            ...(record.isCompanySelected && companyId ? [companyId] : []),
            ...(record.organizationIds || []),
          ])
          .filter(Boolean)
          .map(String),
      );

  return combine(reportingYearScope, disclosureScope);
};

const emptyEntry = () => ({
  id: crypto.randomUUID(),
  label: "",
  entryDate: "",
  value: "",
  valueUnit: "",
  year: "",
  location: "",
  category: "",
  denominator: "",
  reportingMonth: "",
});

const buildInitialSections = (subEntryDefs = []) =>
  subEntryDefs.map((def, i) => ({
    index: i,
    title: def.title || `Sub Entry ${i + 1}`,
    entries: [emptyEntry()],
  }));

const CreateSubEntriesDialog = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { activeDisclosure, reportingBoundary } = useDisclosureStore();
  const { reportingYears } = reportingYearStore;
  const { disclosureOrgTree } = useEntryDataStore();

  const {
    showCreateEntriesDialog,
    creatingEntries,
    setShowCreateEntriesDialog,
  } = useEntryUIStore();
  const {
    errors,
    validateSubEntrySections,
    clearErrors,
    clearEntryFieldError,
  } = useValidationStore();

  const {
    _id,
    code,
    title,
    formula,
    subEntries,
    masterDataYears = [],
    masterDataRecords = [],
  } = activeDisclosure || {};

  const [organizationId, setOrganizationId] = useState("all");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedReportingYearId, setSelectedReportingYearId] = useState("");
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);
  const [showYearError, setShowYearError] = useState(false);
  const [sections, setSections] = useState(() =>
    buildInitialSections(subEntries || []),
  );

  const formValues = useMemo(
    () => ({ sections, organizationId, selectedYear, selectedReportingYearId }),
    [sections, organizationId, selectedYear, selectedReportingYearId],
  );
  const isDirty = useFormDirty(formValues, showCreateEntriesDialog);

  const reportingYearOptions = useMemo(() => {
    const allowedYears = new Set(
      (masterDataYears || [])
        .map((year) => Number(year))
        .filter(Number.isFinite),
    );

    return (reportingYears || [])
      .filter((reportingYear) => {
        const startYear = Number(reportingYear.startYear);

        if (!Number.isFinite(startYear)) return false;

        return allowedYears.has(startYear);
      })
      .sort((a, b) => Number(b.startYear) - Number(a.startYear))
      .map((reportingYear) => ({
        value: String(getReportingYearId(reportingYear)),
        label: formatReportingYearPeriod(reportingYear),
      }));
  }, [masterDataYears, reportingYears]);

  const availableOrgTree = useMemo(() => {
    const allowedSet = buildAllowedOrganizationSet({
      records: masterDataRecords,
      reportingYear: selectedReportingYear,
      selectedYear,
      companyId: user?.companyId,
    });

    return allowedSet
      ? makeTreewithAllowOrgIds(disclosureOrgTree, allowedSet)
      : disclosureOrgTree;
  }, [
    disclosureOrgTree,
    masterDataRecords,
    selectedReportingYear,
    selectedYear,
    user?.companyId,
  ]);

  useEffect(() => {
    if (!showCreateEntriesDialog || selectedReportingYearId || !selectedYear) {
      return;
    }

    const selectedReporting = reportingYears.find((reportingYear) =>
      getYearsInReportingYear(reportingYear).includes(Number(selectedYear)),
    );

    if (!selectedReporting) return;

    setSelectedReportingYearId(String(getReportingYearId(selectedReporting)));
    setSelectedReportingYear(selectedReporting);
  }, [
    reportingYears,
    selectedReportingYearId,
    selectedYear,
    showCreateEntriesDialog,
  ]);

  const yearHasError = showYearError && !selectedYear;
  const yearError = yearHasError ? `${t("It is required")}` : "";

  const formulaTotal = useMemo(() => {
    if (!formula || !subEntries?.length) return null;
    const valueMap = {};
    subEntries.forEach((def, i) => {
      const key = def.formulaKey || `SE${i + 1}`;
      const total = sections[i]?.entries.reduce((sum, e) => {
        const v = parseFloat(e.value);
        return isFinite(v) ? sum + v : sum;
      }, 0);
      if (total) valueMap[key] = total;
    });
    return evaluateFormula(formula, valueMap);
  }, [formula, subEntries, sections]);

  const formulaError = useMemo(() => {
    if (formulaTotal == null) return null;
    if (activeDisclosure?.defaultUnit !== "%") return null;
    if (formulaTotal < 0 || formulaTotal > 100)
      return "Percentage must be between 0% and 100%";
    return null;
  }, [formulaTotal, activeDisclosure?.defaultUnit]);

  const updateEntry = (sectionIndex, entryId, field, value) => {
    clearEntryFieldError(entryId, field);
    setSections((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? {
              ...sec,
              entries: sec.entries.map((e) =>
                e.id === entryId ? { ...e, [field]: value } : e,
              ),
            }
          : sec,
      ),
    );
  };

  const addEntry = (sectionIndex) =>
    setSections((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? { ...sec, entries: [...sec.entries, emptyEntry()] }
          : sec,
      ),
    );

  const removeEntry = (sectionIndex, entryId) =>
    setSections((prev) => {
      const totalEntries = prev.reduce(
        (sum, sec) => sum + sec.entries.length,
        0,
      );
      if (totalEntries <= 1) return prev;
      return prev.map((sec, i) =>
        i === sectionIndex
          ? { ...sec, entries: sec.entries.filter((e) => e.id !== entryId) }
          : sec,
      );
    });

  const canSave = selectedYear && organizationId && organizationId !== "all";

  const doClose = () => {
    setOrganizationId("all");
    setSelectedYear("");
    setSelectedReportingYearId("");
    setSelectedReportingYear(null);
    setShowYearError(false);
    setSections(buildInitialSections(subEntries || []));
    clearErrors();
    setShowCreateEntriesDialog(false);
  };

  const handleClose = (v) => {
    if (!v) {
      doClose();
    }
  };

  const handleSave = async () => {
    const isYearValid = !!selectedYear;
    setShowYearError(!isYearValid);

    if (sections.some((section) => section.entries?.length === 0)) {
      showToast.error("Please fill at lease 1 data at each sub-entry");
      return;
    }
    const areSectionsValid = validateSubEntrySections(
      sections,
      subEntries || [],
      {
        validations: activeDisclosure?.validations,
      },
    );
    if (!isYearValid || !areSectionsValid) return;

    const apiOrgId = toApiOrgId(organizationId, user?.companyId);
    const payload = sections.flatMap((section) =>
      section.entries.map((entry) => {
        const payloadEntry = { ...entry };
        const reportingMonth =
          payloadEntry.reportingMonth ||
          (selectedYear ? `${selectedYear}-01-01` : "");
        delete payloadEntry.id;
        delete payloadEntry.reportingMonth;

        return {
          ...payloadEntry,
          orgId: apiOrgId,
          disclosureId: _id,
          reportingDate: reportingMonth,
          subEntryGroupIndex: section.index,
        };
      }),
    );

    const success = await bulkCreateDataEntries(payload);
    if (success) doClose();
  };

  return (
    <AppDialog
      open={showCreateEntriesDialog}
      onOpenChange={handleClose}
      isDirty={isDirty}
      header={
        <div className="flex items-center gap-2">
          {code && <Badge>{code}</Badge>}
          <span className="text-lg font-semibold">{title}</span>
        </div>
      }
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            {t("Cancel")}
          </Button>
          <Button
            disabled={!canSave || creatingEntries || !!formulaError}
            onClick={handleSave}
          >
            {t("Save Changes")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3 items-start">
          <SelectField
            label={t("Reporting Year")}
            options={reportingYearOptions}
            value={selectedReportingYearId}
            onValueChange={(value) => {
              const selectedReporting = reportingYears.find(
                (ry) => String(getReportingYearId(ry)) === String(value),
              );

              setSelectedReportingYearId(value);
              setSelectedYear(
                selectedReporting?.startYear
                  ? String(selectedReporting.startYear)
                  : "",
              );
              setOrganizationId("all");
              setSelectedReportingYear(selectedReporting || null);
              setShowYearError(false);
            }}
            placeholder={t("Select reporting year")}
            required
            error={yearError}
            emptyLabel={t("No reporting years available")}
            sortOptions={false}
            className="w-full sm:w-60"
          />

          <div className="space-y-2">
            <Label>
              {t("Organization")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <OrgTreeSelect
              tree={availableOrgTree}
              value={organizationId}
              onChange={(v) => {
                setOrganizationId(v);
                setShowYearError(false);
              }}
              placeholder={
                selectedYear
                  ? t("Select organization")
                  : t("Select reporting year first")
              }
              showAllOption={false}
              className="w-full"
              disabled={!selectedYear}
            />
          </div>
        </div>

        {/* Formula banner */}
        {formula && (
          <div
            className={`flex items-center justify-between rounded-lg px-4 py-2.5 border ${formulaError ? "border-destructive text-destructive" : "border-primary text-primary"}`}
          >
            <span className="text-sm font-medium">
              {formulaError || `${t("Total")} ${formula}`}
            </span>
            <span className="text-sm font-semibold">
              {formulaTotal != null
                ? `${formulaTotal}${activeDisclosure?.defaultUnit || ""}`
                : "N/A"}
            </span>
          </div>
        )}

        {/* Sub-entry sections */}
        {sections.map((section) => (
          <div
            key={section.index}
            className="rounded-lg border border-border overflow-hidden"
          >
            {/* Section header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Badge variant="outline">SE{section.index + 1}</Badge>
                <span className="text-sm font-semibold">{section.title}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addEntry(section.index)}
              >
                <Plus size={14} />
                {t("Add Entry")}
              </Button>
            </div>

            {/* Entries table */}
            <EntryRowTable
              entries={section.entries}
              inputType={subEntries?.[section.index]?.inputType || "value"}
              unitOptions={subEntries?.[section.index]?.unitOptions || []}
              errors={errors}
              showInlineErrors
              onUpdate={(entryId, field, value) =>
                updateEntry(section.index, entryId, field, value)
              }
              onRemove={(entryId) => removeEntry(section.index, entryId)}
              masterDataYear={selectedYear}
              reportingBoundary={reportingBoundary}
              selectedReportingYear={selectedReportingYear}
            />
          </div>
        ))}
      </div>
    </AppDialog>
  );
});

export default CreateSubEntriesDialog;
